import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';
import * as XLSX from 'xlsx';

/**
 *  TANTÁRGY-ÖSSZEVONÁS.
 *
 *  Ugyanaz a tantárgy több néven is bekerülhet a törzsadatba („dig.kult." és
 *  „digitális kultúra"), jellemzően különböző importokból. Az órarend egy része az
 *  egyikre, másik része a másikra mutat, a tanmenet kettéválik, az értékelések két
 *  tantárgy alatt gyűlnek.
 *
 *  A képernyő JAVASLATOT ad (hasonló nevek), de a döntést nem veszi el: hogy a kettő
 *  tényleg ugyanaz, azt ember mondja meg. Az összevonás előtt mindig látszik, mi
 *  mozdulna – a végrehajtás pedig visszafordíthatatlan.
 */
@Component({
  selector: 'app-admin-tantargy-osszevonas',
  templateUrl: './tantargy-osszevonas.component.html',
  styleUrls: ['./tantargy-osszevonas.component.scss'],
})
export class AdminTantargyOsszevonasComponent implements OnInit {
  intezmeny = '';
  ev: number | null = null;
  evek: number[] = [];

  toltodik = false;
  hiba = '';
  tantargyak: any[] = [];
  javaslatok: any[] = [];

  forras = '';
  cel = '';
  elonezet: any = null;
  fut = false;

  /*  NÉVBETÖLTÉS a KRÉTA „Tantárgyak minden adata" exportjából.
   *
   *  A KRÉTA három néven ismeri a tantárgyat (név / rövidnév / bizonyítványban
   *  megjelenő név), a törzsadatban viszont kettő fér el – ezért minden import új
   *  tantárgyat csinált a nála lévő néven. A betöltés összevonja, ami már kettévált,
   *  ráírja a bizonyítvány-nevet, a többi nevet pedig ALIASKÉNT rögzíti: ettől fog a
   *  következő import is a meglévő tantárgyra kötni. */
  nevSorok: any[] = [];
  nevFajl = '';
  nevTerv: any = null;
  nevOsszevonas = false;
  nevFut = false;
  nevEredmeny: any = null;

  /** A KRÉTA-export fejlécei. Több elnevezést is elfogadunk – az export változhat. */
  private readonly OSZLOPOK: { [k: string]: string[] } = {
    nev: ['tantárgy név', 'tantárgy neve', 'tantargy nev', 'név', 'megnevezés'],
    rovid: ['tantárgy rövidnév', 'rövidnév', 'rövid név', 'rovidnev'],
    biz: ['bizonyítványban megjelenő név', 'bizonyítvány név', 'bizonyítványban megjelenő megnevezés'],
  };

  private oszlopKulcs(fejlec: string): string | null {
    const f = String(fejlec || '').toLowerCase().trim().replace(/\s+/g, ' ').replace(/:$/, '');
    for (const [kulcs, valtozatok] of Object.entries(this.OSZLOPOK)) {
      if (valtozatok.some((v) => f === v)) return kulcs;
    }
    return null;
  }

  nevFajlValasztva(esemeny: any) {
    const fajl: File = esemeny?.target?.files?.[0];
    this.nevTerv = null;
    this.nevEredmeny = null;
    if (!fajl) return;
    this.nevFajl = fajl.name;
    const olvaso = new FileReader();
    olvaso.onload = (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const nyers: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
        const sorok: any[] = [];
        for (const r of nyers) {
          const sor: any = { nev: '', rovid: '', biz: '' };
          for (const [fejlec, ertek] of Object.entries(r)) {
            const k = this.oszlopKulcs(fejlec);
            if (k) sor[k] = String(ertek ?? '').trim();
          }
          if (sor.nev || sor.biz || sor.rovid) sorok.push(sor);
        }
        this.nevSorok = sorok;
        if (!sorok.length) {
          this.data.sendToast('A fájlban nem találtam „Tantárgy név" oszlopot.', 'error');
          return;
        }
        this.nevElemez();
      } catch (err: any) {
        this.data.sendToast('A fájl nem olvasható: ' + (err?.message || err), 'error');
      }
    };
    olvaso.readAsBinaryString(fajl);
    esemeny.target.value = '';
  }

  async nevElemez() {
    if (!this.nevSorok.length || !this.ev) return;
    this.nevFut = true;
    try {
      this.nevTerv = await firstValueFrom(this.http.post(
        `/api/tantargy-osszevonas/nevek-elemzes/${this.intezmenyPath}`,
        { ev: this.ev, sorok: this.nevSorok }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az elemzés nem futott le.', 'error');
    } finally {
      this.nevFut = false;
    }
  }

  get nevTervSzurt() {
    return (this.nevTerv?.tervek || []).filter((t: any) =>
      t.allapot === 'osszevonas' || t.allapot === 'tobbertelmu');
  }

  async nevBetolt() {
    if (!this.nevTerv) return;
    const o = this.nevTerv.osszesites;
    const uzenet = `${o.atnevezes} tantárgy neve frissül, ${o.alias} alias rögzül.\n`
      + (this.nevOsszevonas
        ? `${o.osszevonas} összevonás fut le – a beolvasztott tantárgyak TÖRLŐDNEK, `
          + 'az óráik, naplójuk és jegyeik átkerülnek. Ez NEM vonható vissza.\n'
        : `${o.osszevonas} összevonást KIHAGYUNK (nincs bepipálva).\n`)
      + `${o.ismeretlen} sorhoz nincs tantárgy – azokat kihagyjuk.\n\nFolytatod?`;
    if (!confirm(uzenet)) return;

    this.nevFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/tantargy-osszevonas/nevek-import/${this.intezmenyPath}`,
        { ev: this.ev, sorok: this.nevSorok, osszevonas: this.nevOsszevonas }));
      this.nevEredmeny = r?.eredmeny || null;
      this.tantargyak = r?.jeloltek?.tantargyak || this.tantargyak;
      this.javaslatok = r?.jeloltek?.javaslatok || [];
      this.data.sendToast('A nevek betöltve.', 'success');
      await this.nevElemez();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A betöltés nem sikerült.', 'error');
    } finally {
      this.nevFut = false;
    }
  }

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    const most = new Date();
    const idei = most.getMonth() + 1 >= 8 ? most.getFullYear() : most.getFullYear() - 1;
    this.evek = [idei + 1, idei, idei - 1, idei - 2];
    this.ev = idei;
    await this.betolt();
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  async betolt() {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.toltodik = true;
    this.hiba = '';
    this.elonezet = null;
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/tantargy-osszevonas/jeloltek/${this.intezmenyPath}` + (this.ev ? `?ev=${this.ev}` : '')));
      this.tantargyak = r?.tantargyak || [];
      this.javaslatok = r?.javaslatok || [];
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A tantárgyak nem tölthetők be.';
      this.tantargyak = []; this.javaslatok = [];
    } finally {
      this.toltodik = false;
    }
  }

  /** Egy javaslatból: a KEVESEBBET használt legyen a beolvasztandó. */
  javaslatValaszt(j: any) {
    const suly = (t: any) => (t.orak || 0) + (t.tanmenet_orak || 0) + (t.ertekelesek || 0);
    const [f, c] = suly(j.a) <= suly(j.b) ? [j.a, j.b] : [j.b, j.a];
    this.forras = f.id;
    this.cel = c.id;
    this.elonezet = null;
    this.elonez();
  }

  nev(id: string): string {
    const t = this.tantargyak.find((x) => String(x.id) === String(id));
    return t ? t.name : '';
  }

  async elonez() {
    if (!this.forras || !this.cel || this.forras === this.cel) { this.elonezet = null; return; }
    this.fut = true;
    try {
      this.elonezet = await firstValueFrom(this.http.post(
        `/api/tantargy-osszevonas/elonezet/${this.intezmenyPath}`,
        { forras: this.forras, cel: this.cel }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az előnézet nem sikerült.', 'error');
      this.elonezet = null;
    } finally {
      this.fut = false;
    }
  }

  async vegrehajt() {
    if (!this.elonezet) return;
    const v = this.elonezet.valtozas;
    if (!confirm(`„${this.elonezet.forras.name}" beolvasztása ide: „${this.elonezet.cel.name}".\n\n`
      + `${v.orarend} óra, ${v.tanmenet_orak} tanmenet-sor, ${v.ertekelesek} értékelés kerül át.\n`
      + (v.orarend_utkozes_torolve ? `${v.orarend_utkozes_torolve} ütköző (naplózatlan) óra törlődik.\n` : '')
      + (v.orarend_utkozes_naplozott ? `${v.orarend_utkozes_naplozott} ütköző óra NAPLÓZOTT – az megmarad.\n` : '')
      + `\nA beolvasztott tantárgy törlődik. Ez NEM vonható vissza.\n\nFolytatod?`)) return;

    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/tantargy-osszevonas/vegrehajt/${this.intezmenyPath}`,
        { forras: this.forras, cel: this.cel, ev: this.ev }));
      this.tantargyak = r?.jeloltek?.tantargyak || this.tantargyak;
      this.javaslatok = r?.jeloltek?.javaslatok || [];
      this.forras = ''; this.elonezet = null;
      this.data.sendToast('Az összevonás megtörtént.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az összevonás nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }
}
